import Image from "next/image"
import Link from "next/link"
import type { ComponentProps } from "react"
import type {
  OrderItemDetail,
  OrderTotalLine,
} from "@/api/account-order-detail"
import { cn } from "@/utilities"
import { ReviewActions } from "./review-actions"

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  }).format(value)
}

interface OrderItemsTableProps extends ComponentProps<"div"> {
  currency: string
  customerNote?: string
  items: OrderItemDetail[]
  totals: OrderTotalLine[]
}

export function OrderItemsTable({
  items,
  totals,
  currency,
  customerNote,
  className,
  ...props
}: OrderItemsTableProps) {
  return (
    <div className={cn("space-y-4", className)} {...props}>
      <h2 className='font-semibold text-foreground text-xl'>
        Detalhes do pedido
      </h2>

      <div className='overflow-x-auto'>
        <table className='min-w-full border-collapse text-left text-sm'>
          <thead>
            <tr className='border-border border-b'>
              <th
                className='px-3 py-3 font-semibold text-foreground'
                scope='col'
              >
                Produto
              </th>
              <th
                className='px-3 py-3 font-semibold text-foreground'
                scope='col'
              >
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {items.map((item) => (
              <tr
                className='border-border border-b last:border-b-0'
                key={item.id}
              >
                <td className='px-3 py-3'>
                  <div className='flex items-start gap-3'>
                    {item.image && (
                      <div className='relative size-16 shrink-0 overflow-hidden rounded-md border border-border'>
                        <Image
                          alt={item.name}
                          className='object-cover'
                          fill
                          src={item.image}
                        />
                      </div>
                    )}
                    <div className='flex-1 space-y-1'>
                      {item.productPermalink ? (
                        <Link
                          className='font-medium text-foreground hover:underline'
                          href={item.productPermalink}
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <p className='font-medium text-foreground'>
                          {item.name}
                        </p>
                      )}
                      {item.sku && (
                        <p className='text-muted-foreground text-xs'>
                          SKU: {item.sku}
                        </p>
                      )}
                      {item.variationAttributes &&
                        item.variationAttributes.length > 0 && (
                          <div className='space-y-0.5'>
                            {item.variationAttributes.map((attr, index) => (
                              <p
                                className='text-muted-foreground text-xs'
                                key={`${item.id}-${attr.name}-${index}`}
                              >
                                {attr.name}: {attr.value}
                              </p>
                            ))}
                          </div>
                        )}
                      <p className='text-muted-foreground text-sm'>
                        Quantidade: {item.quantity}
                      </p>
                      {item.productId && item.productPermalink && (
                        <ReviewActions
                          productId={item.productId}
                          productPermalink={item.productPermalink}
                        />
                      )}
                    </div>
                  </div>
                </td>
                <td className='px-3 py-3 text-foreground'>
                  {formatCurrency(item.total, currency)}
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            {totals.map((total, index) => {
              const isTotal = total.label === "Total"
              return (
                <tr
                  className='border-border border-t'
                  key={`${total.label}-${index}`}
                >
                  <th
                    className={cn(
                      "px-3 py-3 text-right font-semibold text-foreground",
                      isTotal && "text-lg"
                    )}
                    scope='row'
                  >
                    {total.label}:
                  </th>
                  <td
                    className={cn(
                      "px-3 py-3 text-foreground",
                      isTotal && "font-semibold text-lg"
                    )}
                  >
                    {total.value}
                  </td>
                </tr>
              )
            })}
            {customerNote && (
              <tr className='border-border border-t'>
                <th
                  className='px-3 py-3 text-right font-semibold text-foreground'
                  scope='row'
                >
                  Observação:
                </th>
                <td className='px-3 py-3 text-foreground'>{customerNote}</td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  )
}
